import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Search, ShoppingCart, Settings, User, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import USER_PATHS from "../USER_PATHS";
import SearchBar from "./SearchBar";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { addToCart, checkout, clearCart, MAX_CART_ITEM_QUANTITY, removeFromCart, updateQuantity } from "../../../features/cartSlice";
import { toast } from "react-toastify";
import { convertAmount, formatMoney as fmtMoney, normalizeCurrencyRates } from "../../../util/money";

const TRENDING_TERMS = [
  "Sneakers",
  "Wireless earbuds",
  "Smart watch",
  "Summer dress",
  "Phone cases",
  "Backpacks",
];

const MobileBottomBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [searchOpen, setSearchOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const navItemClass = (active) =>
    `flex flex-col items-center gap-1 text-xs font-semibold ${active ? "text-[#D2042D]" : "text-slate-700"}`;
  const isHomeActive = location.pathname === "/" || location.pathname === "";
  const isSearchActive = searchOpen || location.pathname.startsWith("/search");
  const isCartActive = cartOpen || location.pathname.startsWith("/cart");

  const cartItemsRaw = useSelector((state) => state?.cart?.items);
  const authToken = useSelector((state) => state?.userAuth?.token);
  const items = useMemo(() => cartItemsRaw || [], [cartItemsRaw]);
  const selectedCurrency = useSelector((state) => state?.userSettings?.currency) || "USD";
  const currencyPayload = useSelector((state) => state?.currency?.currency_list);
  const rates = useMemo(() => normalizeCurrencyRates(currencyPayload), [currencyPayload]);

  const count = Array.isArray(items)
    ? items.reduce((sum, it) => sum + Number(it?.quantity || 0), 0)
    : 0;

  const money = (amount, from = "USD") =>
    fmtMoney(amount, { from, to: selectedCurrency || "USD", rates });

  const displaySubtotal = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    return list.reduce((s, it) => {
      const from = it?.currency || it?.price_currency || it?.currency_code || "USD";
      const price = Number(it?.price || 0);
      const qty = Number(it?.quantity || 0);
      const line = Number.isFinite(price * qty) ? price * qty : 0;
      return s + convertAmount(line, { from, to: selectedCurrency || "USD", rates });
    }, 0);
  }, [items, selectedCurrency, rates]);

  useEffect(() => {
    if (!searchOpen && !cartOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSearchOpen(false);
        setCartOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchOpen, cartOpen]);

  useEffect(() => {
    const lock = searchOpen || cartOpen;
    if (!lock) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      if (prev !== undefined) {
        document.body.style.overflow = prev;
      }
    };
  }, [searchOpen, cartOpen]);

  useEffect(() => {
    setSearchOpen(false);
    setCartOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleOpenSearch = () => {
      setCartOpen(false);
      setSearchOpen(true);
    };
    window.addEventListener("cherrfy:open-mobile-search", handleOpenSearch);
    return () => window.removeEventListener("cherrfy:open-mobile-search", handleOpenSearch);
  }, []);

  const openSettings = () => {
    setSearchOpen(false);
    setCartOpen(false);
    window.dispatchEvent(new Event("cherrfy:open-settings"));
  };

  const openProfile = () => {
    setSearchOpen(false);
    setCartOpen(false);
    window.dispatchEvent(new Event("cherrfy:open-profile-drawer"));
  };

  const handleSearchNavigate = (term) => {
    const trimmed = String(term || "").trim();
    if (!trimmed) return;
    navigate(`/search/${encodeURIComponent(trimmed)}`);
    setSearchOpen(false);
  };

  const goCheckout = () => {
    if (!items?.length) return;
    dispatch(checkout(items));
    setCartOpen(false);
    if (!authToken) {
      // Anonymous tap used to be yanked by PrivateRoute with no message;
      // land on the cart instead, where the account caption explains why.
      toast.info("Add your address after signing in — your cart is saved.");
      navigate(USER_PATHS.CART);
      return;
    }
    navigate(USER_PATHS.CHECKOUT);
  };

  const goCart = () => {
    setCartOpen(false);
    navigate(USER_PATHS.CART);
  };

  const content = (
    <>
      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 z-[120]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="mx-3 mb-3 rounded-3xl border border-slate-200 bg-white/95 shadow-none backdrop-blur">
          <div className="grid grid-cols-5 items-center px-3 py-2" role="navigation">
            <Link
              to={USER_PATHS.HOME}
              onClick={() => {
                setSearchOpen(false);
                setCartOpen(false);
                window.dispatchEvent(new Event("cherrfy:close-settings"));
              }}
              aria-current={isHomeActive ? "page" : undefined}
              className={navItemClass(isHomeActive)}
            >
              <Home size={18} />
              Home
            </Link>

            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-current={isSearchActive ? "page" : undefined}
              className={navItemClass(isSearchActive)}
            >
              <Search size={18} />
              Search
            </button>

            <button
              type="button"
              onClick={() => setCartOpen(true)}
              className="relative -translate-y-4 mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#D2042D] text-white shadow-none ring-4 ring-white/80"
              aria-label={`Open cart${count > 0 ? ` with ${count} item${count === 1 ? "" : "s"}` : ""}`}
              aria-current={isCartActive ? "page" : undefined}
            >
              <ShoppingCart size={20} />
              {count > 0 ? (
                <span className="absolute -right-1 -top-1 inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-white px-1 text-[10px] font-semibold text-[#D2042D]">
                  {count}
                </span>
              ) : null}
            </button>

            <button
              type="button"
              onClick={openSettings}
              className="flex flex-col items-center gap-1 text-xs font-semibold text-slate-700"
            >
              <Settings size={18} />
              Settings
            </button>

            <button
              type="button"
              onClick={openProfile}
              className="flex flex-col items-center gap-1 text-xs font-semibold text-slate-700"
            >
              <User size={18} />
              Profile
            </button>
          </div>
        </div>
      </div>

      {searchOpen ? (
        <div className="fixed inset-0 z-[110] bg-transparent">
          <div className="relative flex h-full flex-col">
            <div className="bg-white">
              <div className="flex items-center justify-between px-4 py-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">Search</h2>
                <button
                  type="button"
                  onClick={() => setSearchOpen(false)}
                  className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-slate-200"
                  aria-label="Close search"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="px-4 py-4">
                <SearchBar className="w-full" autoFocusInput />
              </div>
            </div>

            <div
              className="relative flex-1 px-4 pb-6 pt-4 bg-black/5 backdrop-blur-sm"
              onClick={() => setSearchOpen(false)}
            >
              <div className="absolute left-0 right-0 top-0 h-4 bg-gradient-to-b from-white to-transparent" />
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Trending</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {TRENDING_TERMS.map((term) => (
                  <button
                    key={term}
                    type="button"
                    onClick={() => handleSearchNavigate(term)}
                    className="rounded-2xl border border-slate-200/70 bg-white/80 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-white/95"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {cartOpen ? (
        <div className="fixed inset-0 z-[180] bg-white">
          <div className="flex items-center justify-between px-4 py-4 border-b border-slate-200">
            <div>
              <h2 className="text-lg font-bold text-slate-900">My Cart</h2>
              <p className="text-xs text-slate-500">{count} item(s)</p>
            </div>
            <button
              type="button"
              onClick={() => setCartOpen(false)}
              className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-slate-200"
              aria-label="Close cart"
            >
              <X size={18} />
            </button>
          </div>

          <div className="px-4 py-4 space-y-3 overflow-y-auto" style={{ maxHeight: "calc(100vh - 240px)" }}>
            {items.length === 0 ? (
              <div className="py-10 text-center">
                <p className="text-sm text-slate-500">Your cart is empty.</p>
                <p className="mt-1 text-xs text-slate-400">Add products to see them here.</p>
              </div>
            ) : (
              items.map((it) => {
                const from = it?.currency || it?.price_currency || it?.currency_code || "USD";
                const price = Number(it?.price || 0);
                const qty = Number(it?.quantity || 1);
                const imgSrc =
                  typeof it?.image === "string" && it.image.trim()
                    ? it.image
                    : typeof it?.imageUrl === "string" && it.imageUrl.trim()
                      ? it.imageUrl
                      : "/placeholder.svg";

                return (
                  <div key={it.id} className="flex gap-3 rounded-2xl border border-slate-200 p-3">
                    <img
                      src={imgSrc}
                      alt={it?.name || "Item"}
                      className="h-16 w-16 rounded-xl object-cover border flex-shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900 truncate" title={it?.name}>
                        {it?.name || "Item"}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500">{money(price, from)} each</p>
                      <div className="mt-2 flex items-center gap-3">
                        <div className="inline-flex items-center rounded-xl border border-slate-200 overflow-hidden">
                          <button
                            type="button"
                            onClick={() =>
                              dispatch(
                                updateQuantity({
                                  id: it.id,
                                  quantity: Math.max(1, qty - 1),
                                })
                              )
                            }
                            className="h-8 w-8 inline-flex items-center justify-center text-slate-700"
                            aria-label="Decrease quantity"
                          >
                            −
                          </button>
                          <span className="h-8 min-w-[36px] inline-flex items-center justify-center text-sm font-semibold text-slate-800">
                            {qty}
                          </span>
	                          <button
	                            type="button"
	                            onClick={() => dispatch(updateQuantity({ id: it.id, quantity: qty + 1 }))}
	                            disabled={qty >= MAX_CART_ITEM_QUANTITY}
	                            className="h-8 w-8 inline-flex items-center justify-center text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
	                            aria-label="Increase quantity"
	                          >
                            +
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => dispatch(removeFromCart(it.id))}
                          className="text-xs font-semibold text-rose-600"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    <div className="text-sm font-bold text-slate-900">{money(price * qty, from)}</div>
                  </div>
                );
              })
            )}
          </div>

          <div className="border-t border-slate-200 px-4 py-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Subtotal</span>
              <span className="font-bold text-slate-900">{money(displaySubtotal, selectedCurrency || "USD")}</span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={goCart}
                className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800"
              >
                View Cart
              </button>
              <button
                type="button"
                onClick={goCheckout}
                disabled={items.length === 0}
                className="inline-flex items-center justify-center rounded-2xl bg-[#D2042D] px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
              >
                Checkout
              </button>
            </div>
            {items.length > 0 ? (
              <button
                type="button"
                onClick={() => setConfirmClearOpen(true)}
                className="mt-3 w-full rounded-2xl px-4 py-2 text-xs font-semibold text-slate-600"
              >
                Clear cart
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        isOpen={confirmClearOpen}
        title="Clear your cart?"
        message="This removes every item from your cart. You can undo this for a few seconds."
        confirmText="Clear cart"
        cancelText="Keep items"
        onCancel={() => setConfirmClearOpen(false)}
        onConfirm={() => {
          const snapshot = Array.isArray(items) ? items : [];
          dispatch(clearCart());
          setConfirmClearOpen(false);
          if (snapshot.length) {
            toast.info(
              (
                <span>
                  Cart cleared{" "}
                  <button
                    type="button"
                    onClick={() => {
                      snapshot.forEach((row) => dispatch(addToCart(row)));
                      toast.dismiss("cart-remove-undo");
                    }}
                    className="font-bold underline underline-offset-2"
                  >
                    Undo
                  </button>
                </span>
              ),
              { toastId: "cart-remove-undo", autoClose: 8000 }
            );
          }
        }}
      />
    </>
  );

  if (typeof document === "undefined") return content;
  return createPortal(content, document.body);
};

export default MobileBottomBar;
